import {SingleTokenPriceInput} from '#/atoms/single-token-price-input';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {ActionMenuOverviewInfoRow} from '#/components/actionmenu/action-menu-overview';
import {ActionMenuContentSectionOverview} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {MessageCase} from '#/components/actionmenu/types';
import {MaxButton} from '#/components/buttons/max-button';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {useQueryParamsTabKey} from '#/hooks/use-query-params-tab';
import {RentTab} from '#/navigation/tabs';
import {stringToTokenAmount, tokenAmountToInputString} from '#/utils/token-string-utils';
import {DefaultChildren} from '#/utils/utils';
import React, {FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TransactionType, liquidityReceivedForLiquidityProviderToken} from 'shareland-common';

export const ActionMenuRentStopRentTab: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {landMarketInfo, landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(TransactionType.BurnLiquidityToken);

  // Internal hooks
  const [lpTokenInput, setLpTokenInput] = useState('');
  const lpTokenAmount = stringToTokenAmount(lpTokenInput);

  // Vars
  const viewOnly = landAccountInfo.lpTokenAmount === 0;
  const {landTokenAmount, stableTokenAmount} = liquidityReceivedForLiquidityProviderToken(
    lpTokenAmount,
    landMarketInfo,
  );
  const transactionVars: TransactionVariables[TransactionType.BurnLiquidityToken] = {
    landTokenAmount,
    stableTokenAmount,
    lpTokenAmount,
  };

  const noticeMessages = useNoticeMessage(transactionVars);
  const canStartTransactionFlow = lpTokenAmount !== 0 && noticeMessages.every(elem => elem.canSubmitForm);

  // Functions
  const onTokenAmountChange = (amount: string) => {
    setLpTokenInput(amount);
  };

  const onMaxAmountButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setLpTokenInput(tokenAmountToInputString(landAccountInfo.lpTokenAmount));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: TransactionType.BurnLiquidityToken,
      data: transactionVars,
    });
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && (
            <ActionMenuInfoTile onClose={onCloseTile} tileKey={TransactionType.BurnLiquidityToken} />
          ),
          grow: (
            <>
              <SingleTokenPriceInput
                className={viewOnly ? 'view-only' : ''}
                title={'Burn'}
                tokenName={landMarketInfo.lpSymbol}
                tokenAmount={lpTokenInput}
                onChange={onTokenAmountChange}
              />
              <div className='flex justify-end mt-3'>
                <MaxButton
                  className='text-white disabled:grayscale disabled:pointer-events-none'
                  disabled={lpTokenAmount === landAccountInfo.lpTokenAmount}
                  onClick={onMaxAmountButtonClick}
                />
              </div>
            </>
          ),
          overview: (
            <ActionMenuContentSectionOverview className={viewOnly ? 'view-only' : ''}>
              <ActionMenuOverviewInfoRow.Burn lpTokenAmount={transactionVars.lpTokenAmount} />
              <ActionMenuOverviewInfoRow.ReceiveLand landTokenAmount={transactionVars.landTokenAmount} />
              <ActionMenuOverviewInfoRow.ReceiveStable stableTokenAmount={transactionVars.stableTokenAmount} />
            </ActionMenuContentSectionOverview>
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton disabled={!canStartTransactionFlow} />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useNoticeMessage = (transactionVars: TransactionVariables[TransactionType.BurnLiquidityToken]) => {
  const {land, landMarketInfo, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');
  const {setTab} = useQueryParamsTabKey({queryParamTabKey: 'rentTabId', tabKeys: RentTab});

  const cases: Record<string, MessageCase> = {
    noRentToStop: {
      condition: () => landAccountInfo.lpTokenAmount === 0,
      message: () =>
        actionMenuMessageContent.mustRentToStopRent(t, land.tokenSymbol, () => {
          setTab(RentTab.RENT);
        }),
    },
    notEnoughLpToken: {
      condition: () => transactionVars.lpTokenAmount > landAccountInfo.lpTokenAmount,
      message: () => actionMenuMessageContent.notEnoughTokenAmount(t, landMarketInfo.lpSymbol),
    },
  };

  return useMessageCasesToContent(cases);
};
